import { useMemo } from 'react';
import { useLanguage } from 'context/AppContext';
import theme from 'App.theme';
import { ActionItems, ActionItemType, TableTypes } from '../types';
import { addBusinessDays, formatDateToYYYYMMDD, subtractBusinessDays } from 'utils/helpers/DateTimeFormat.helpers';

const formattedBeginningOfTime = formatDateToYYYYMMDD(0);

const useActionItems = () => {
  const lang = useLanguage()[0].ActionItemsPage.ActionItemsMenu;

  const dateNow = new Date();

  const formattedDateNowPlus1Day = formatDateToYYYYMMDD(addBusinessDays(dateNow, 1));
  const formattedDateNowPlus2Days = formatDateToYYYYMMDD(addBusinessDays(dateNow, 2));
  const formattedDateNowMinus24Hours = formatDateToYYYYMMDD(subtractBusinessDays(dateNow, 1));
  const formattedDateNowPlus24Hours = formatDateToYYYYMMDD(addBusinessDays(dateNow, 1));

  const actionItems = useMemo<ActionItems>(
    () => ({
      [ActionItemType.CriticalItems]: {
        lang: lang.criticalItems,
        colors: theme.colors.statuses.DELETED,
        tableType: TableTypes.CRITICAL_ITEMS,
        filters: {
          sort_algorithm: 'critical_items',
          status: ['OUTBOUND', 'RECEIVED', 'PARTIALLY_CHECKED_IN'],
          lower_surgery_date: formattedBeginningOfTime,
          upper_surgery_date: formattedDateNowPlus1Day,
        },
      },

      [ActionItemType.BookingsToBeConfirmed]: {
        lang: lang.bookingsToBeConfirmed,
        colors: theme.colors.statuses.DISPATCHED,
        dataType: 'orders',
        // disabled for now because this is needs a search on
        //  the orders table when we have the booking feature
      },

      [ActionItemType.DeliveriesToBeReceived]: {
        lang: lang.deliveriesToBeReceived,
        colors: theme.colors.statuses.RECEIVED,
        tableType: TableTypes.RECEIVE_OVERDUE,
        filters: {
          status: ['OUTBOUND', 'DELIVERED'],
          lower_surgery_date: formattedBeginningOfTime,
          upper_surgery_date: formattedDateNowPlus2Days,
        },
      },

      [ActionItemType.DeliveriesToBeCheckedIn]: {
        lang: lang.deliveriesToBeCheckedIn,
        colors: theme.colors.statuses.CHECKED_IN,
        tableType: TableTypes.CHECK_IN_OVERDUE,
        filters: {
          sort_algorithm: 'awaiting_check_in',
          status: 'OVERDUE_FOR_CHECK_IN',
          lower_surgery_date: formattedBeginningOfTime,
          upper_surgery_date: formattedDateNowPlus24Hours,
        },
      },

      [ActionItemType.DeliveriesToBeCheckedOut]: {
        lang: lang.deliveriesToBeCheckedOut,
        colors: theme.colors.statuses.CHECKED_OUT,
        tableType: TableTypes.CHECK_OUT_OVERDUE,
        filters: {
          sort_algorithm: 'awaiting_check_out',
          status: 'OVERDUE_FOR_CHECK_OUT',
          lower_surgery_date: formattedBeginningOfTime,
          upper_surgery_date: formattedDateNowMinus24Hours,
        },
      },
    }),
    [
      formattedDateNowMinus24Hours,
      formattedDateNowPlus1Day,
      formattedDateNowPlus24Hours,
      formattedDateNowPlus2Days,
      lang.bookingsToBeConfirmed,
      lang.criticalItems,
      lang.deliveriesToBeCheckedIn,
      lang.deliveriesToBeCheckedOut,
      lang.deliveriesToBeReceived,
    ],
  );

  return actionItems;
};

export default useActionItems;
